import React from "react";
import { APP_CONFIG, GOOGLE_MAP_MODE_NAMES } from "../../constants/constants";
import {
  TLatLng,
  TMapLayerOptions,
  TMapState,
  TMapType,
} from "../../types/map";
import { TMapRef as TMapRefFleet } from "../../pages/map-fleet/type";
import { TMapRef as TMapRefVehicle } from "../../pages/map-asset/type";
import { TMapRef as TMapRefGeozone } from "../../pages/admin-details/geozone/type";
import { useGMapMode } from "../../hooks/mapGoogle";
import { useMapInit } from "../../hooks/mapInit";
import { useDispatch, useSelector } from "react-redux";
import {
  setUserMapActivity,
  TUserMapActivity,
} from "../../api/store/commonSlice";
import { LegacyOrganizationAccount } from "../../api/types/Account";
import { localStorageKeys } from "../../utils/localStorageItems";

export const resolveGoogleMapIdFromAccount = (
  accountJson: string | null,
  environmentMapId: string | undefined
): string | undefined => {
  try {
    if (accountJson) {
      const ownerOrganization: LegacyOrganizationAccount =
        JSON.parse(accountJson);
      if (ownerOrganization.mapid) {
        return ownerOrganization.mapid;
      }
    }
  } catch {
    // fall through to default
  }
  return environmentMapId;
};

type TOnMapScriptLoadedProps = {
  mapRef: React.MutableRefObject<
    TMapRefFleet | TMapRefVehicle | TMapRefGeozone
  >;
  mapStateToSetToLastViewOnLoad: TMapState | null; // set to last view if user is on fleet map page - prevent momentary flicker to default location
  mapType: TMapType;
  mapLayerOptions: TMapLayerOptions;
  currentPosition: TLatLng;
  setLoadingMap: React.Dispatch<React.SetStateAction<boolean>>;
  onMapReadyCallback?: () => void;
  recordActivityAsPage?: "fleetMap" | "assetMap" | "heatMap";
  nativeMapControls?: boolean;
};

export const useMapScriptLoadedHandler = () => {
  const { gMapSetMode } = useGMapMode();
  const { gIdentifyAndSetMapModeToStore } = useMapInit();
  const dispatch = useDispatch();

  const userMapActivity = useSelector(
    (state: { commonReducer: { userMapActivity: TUserMapActivity } }) =>
      state.commonReducer.userMapActivity
  );
  const userMapRefreshing = useSelector(
    (state: { commonReducer: { userMapRefreshing: boolean } }) =>
      state.commonReducer.userMapRefreshing
  );

  const resolveGoogleMapId = (): string | undefined => {
    const ownerOrganizationRaw = window.localStorage.getItem(
      localStorageKeys.ownerOrganization
    );
    return resolveGoogleMapIdFromAccount(
      ownerOrganizationRaw,
      process.env.REACT_APP_GOOGLE_MAP_ID
    );
  };

  const onMapScriptLoaded = (props: TOnMapScriptLoadedProps) => {
    if (APP_CONFIG.DEBUG.MAPS) {
      console.log("onMapScriptLoaded - props.mapType:", props.mapType);
      console.log(
        "onMapScriptLoaded - mapTypeId will be:",
        props?.mapType ?? APP_CONFIG.MAPS.DEFAULTS.TYPE
      );
    }

    const google = (window as any).google;
    if (!google) {
      return;
    }
    var mapOptions = {
      center: {
        lat:
          props?.mapStateToSetToLastViewOnLoad?.mapCenter?.latitude ??
          props.currentPosition.latitude,
        lng:
          props?.mapStateToSetToLastViewOnLoad?.mapCenter?.longitude ??
          props.currentPosition.longitude,
      },
      zoom:
        props?.mapStateToSetToLastViewOnLoad?.mapZoom ??
        APP_CONFIG.MAPS.DEFAULTS.ZOOM,
      minZoom: APP_CONFIG.MAPS.ZOOM_MIN,
      maxZoom: APP_CONFIG.MAPS.ZOOM_MAX,
      disableDefaultUI: true, // Disable default UI controls
      ...(props?.nativeMapControls ? { cameraControl: true } : {}),
      mapTypeId: props?.mapType ?? (APP_CONFIG.MAPS.DEFAULTS.TYPE as TMapType), // roadmap, satellite, hybrid, terrain
      mapId: resolveGoogleMapId(), // account-level override or env var fallback
    };

    if (APP_CONFIG.DEBUG.MAPS)
      console.log("onMapScriptLoaded - full mapOptions:", mapOptions);

    // render the map
    try {
      props.mapRef.current.map = new google.maps.Map(
        document.getElementById(APP_CONFIG.MAPS.COMPONENT_ID),
        mapOptions
      );
      // if birds eye or street view is needed on init (esp. map refresh), set those options
      switch (props.mapType) {
        case GOOGLE_MAP_MODE_NAMES.BIRDSEYE:
          gMapSetMode(props.mapRef.current.map, GOOGLE_MAP_MODE_NAMES.BIRDSEYE);
          break;
        case GOOGLE_MAP_MODE_NAMES.STREETVIEW:
          gMapSetMode(
            props.mapRef.current.map,
            GOOGLE_MAP_MODE_NAMES.STREETVIEW,
            props.mapRef.current.map.getCenter(),
            {
              // callback checks if streetside mode is enabled and sets applicable map type in store
              onStreetViewVisibleChanged: (panoVisible: boolean) => {
                if (APP_CONFIG.DEBUG.MAPS)
                  console.log(
                    "[onStreetViewVisibleChanged] attached at onMapScriptLoaded.ts"
                  );
                const newMapMode = gIdentifyAndSetMapModeToStore(
                  props.mapRef.current.map
                );
                if (APP_CONFIG.DEBUG.MAPS)
                  console.log("SVCHANGED newMapMode:", newMapMode);
                const panorama = props.mapRef.current.map.getStreetView();
                setTimeout(() => {
                  if (!panorama.getVisible()) {
                    // exited streetview
                    const newMapMode = gIdentifyAndSetMapModeToStore(
                      props.mapRef.current.map
                    );
                    if (APP_CONFIG.DEBUG.MAPS)
                      console.log(
                        "SVCHANGED - pano not visible (exited) newMapMode:",
                        newMapMode
                      );
                    // update user map activity in store
                    // if (!recordActivityAsPage || refreshingRef.current) return;
                    if (userMapRefreshing) {
                      console.log(
                        "SVCHANGED - NO - userMapRefreshing",
                        userMapRefreshing,
                        "RETURNING"
                      );
                      return;
                    }
                    if (APP_CONFIG.DEBUG.MAPS)
                      console.log(
                        "SVCHANGED - about to trigger usermapactivity change",
                        newMapMode
                      );
                    // check if streetview exit was not successful, trigger change to roadmap
                    if (newMapMode === "streetview") {
                      if (APP_CONFIG.DEBUG.MAPS)
                        console.log(
                          "SVCHANGED - fix incorrect 'newMapMode' value from 'streetview' to ",
                          GOOGLE_MAP_MODE_NAMES.ROADMAP
                        );
                      gMapSetMode(
                        props.mapRef.current.map,
                        GOOGLE_MAP_MODE_NAMES.ROADMAP,
                        props.mapRef.current.map.getCenter()
                      );
                    }
                    setTimeout(() => {
                      const newMapMode = gIdentifyAndSetMapModeToStore(
                        props.mapRef.current.map
                      );
                      if (APP_CONFIG.DEBUG.MAPS)
                        console.log(
                          "SVCHANGED - about to trigger setUserMapActivity change - newMapMode:",
                          newMapMode
                        );
                      if (!!props.recordActivityAsPage) {
                        dispatch(
                          setUserMapActivity({
                            ...userMapActivity,
                            [props.recordActivityAsPage as "fleetMap"]: {
                              // ...activityRef.current?.fleetMap,
                              ...userMapActivity?.[
                                props.recordActivityAsPage as "fleetMap"
                              ],
                              center: {
                                latitude: props.mapRef.current.map
                                  .getCenter()
                                  .lat(),
                                longitude: props.mapRef.current.map
                                  .getCenter()
                                  .lng(),
                              } as TLatLng,
                              zoom: props.mapRef.current.map.getZoom(),
                              mapType: newMapMode,
                            },
                          } as TUserMapActivity)
                        );
                      }
                    }, 600);
                  } else {
                    if (APP_CONFIG.DEBUG.MAPS)
                      console.log("SVCHANGED - pano is visible");
                  }
                }, 600);
              },
            }
          );
          break;
      }
    } catch (e) {
      if (APP_CONFIG.DEBUG.ALL)
        console.error("An error occurred while initializing the map:", e);
    }

    // assign map to global variable
    (window as any).googleMap = props.mapRef.current.map;

    // set map layer options
    if (props.mapLayerOptions.traffic) {
      const trafficLayer = new google.maps.TrafficLayer();
      trafficLayer.setMap(props.mapRef.current.map);
    }

    props.setLoadingMap(false);
    props.onMapReadyCallback && props.onMapReadyCallback();
  };
  return { onMapScriptLoaded };
};
